import React, { useMemo, useState } from 'react';
import { Calculator, Printer } from 'lucide-react';
import LaborOperationsLayout from '../../../components/labor/LaborOperationsLayout';

const initialForm = {
  startDate: '',
  endDate: '',
  basicWage: '',
  totalWage: '',
  endReason: 'employer_termination',
};

const reasonLabels = {
  resignation: 'استقالة',
  employer_termination: 'إنهاء من صاحب العمل',
  contract_end: 'انتهاء عقد',
  other: 'غير ذلك',
};

const toNumber = (value) => Number(String(value || '').replace(/,/g, '')) || 0;
const money = (value) => `${new Intl.NumberFormat('ar-SA', { maximumFractionDigits: 2 }).format(value || 0)} ريال`;

const calculateService = (startDate, endDate) => {
  if (!startDate || !endDate) return { days: 0, years: 0 };
  const start = new Date(startDate);
  const end = new Date(endDate);
  const days = Math.max(0, Math.floor((end - start) / (24 * 60 * 60 * 1000)) + 1);
  return { days, years: days / 365.25 };
};

const EndOfServiceCalculatorPage = () => {
  const [form, setForm] = useState(initialForm);

  const result = useMemo(() => {
    const wage = toNumber(form.totalWage) || toNumber(form.basicWage);
    const service = calculateService(form.startDate, form.endDate);
    const firstFiveYears = Math.min(service.years, 5);
    const followingYears = Math.max(service.years - 5, 0);
    const fullAward = wage * ((firstFiveYears * 0.5) + followingYears);
    let entitlementRate = 1;
    let entitlementNote = 'تستحق المكافأة كاملة عند انتهاء العلاقة لغير الاستقالة وفق قاعدة المادة 84.';

    if (form.endReason === 'resignation') {
      if (service.years < 2) {
        entitlementRate = 0;
        entitlementNote = 'في الاستقالة قبل سنتين لا تظهر مكافأة مستحقة وفق المادة 85.';
      } else if (service.years <= 5) {
        entitlementRate = 1 / 3;
        entitlementNote = 'في الاستقالة من سنتين إلى خمس سنوات يستحق العامل ثلث المكافأة.';
      } else if (service.years < 10) {
        entitlementRate = 2 / 3;
        entitlementNote = 'في الاستقالة بعد أكثر من خمس سنوات وأقل من عشر سنوات يستحق العامل ثلثي المكافأة.';
      } else {
        entitlementRate = 1;
        entitlementNote = 'في الاستقالة بعد عشر سنوات فأكثر يستحق العامل المكافأة كاملة.';
      }
    }

    return {
      wage,
      service,
      fullAward,
      entitlementRate,
      award: fullAward * entitlementRate,
      entitlementNote,
    };
  }, [form]);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  return (
    <LaborOperationsLayout>
      <main className="mx-auto max-w-[1200px] px-6 pb-14 pt-10 sm:px-12" dir="rtl">
        <section className="lo-hero px-1 pb-8 pt-2">
          <p className="lo-kicker font-bold">END OF SERVICE</p>
          <h1 className="lo-heading legal-title mt-4 font-bold">حاسبة مكافأة نهاية الخدمة</h1>
          <p className="lo-lead mt-3">أداة تشغيلية داخلية للتقدير الأولي وفق قواعد نظام العمل السعودي، ولا تعد اعتماداً نهائياً للمطالبة.</p>
        </section>

        <section className="grid gap-6 lg:grid-cols-[1fr_420px]">
          <div className="lo-surface rounded-[20px] p-7">
            <div className="mb-6 flex items-center gap-3">
              <Calculator className="h-5 w-5 text-[#948066]" />
              <h2 className="legal-title text-xl font-bold text-[#293a50]">بيانات الحساب</h2>
            </div>
            <div className="grid gap-5 md:grid-cols-2">
              <Field label="تاريخ المباشرة" type="date" value={form.startDate} onChange={(value) => update('startDate', value)} />
              <Field label="تاريخ انتهاء العلاقة" type="date" value={form.endDate} onChange={(value) => update('endDate', value)} />
              <Field label="الأجر الأساسي" inputMode="decimal" value={form.basicWage} onChange={(value) => update('basicWage', value)} />
              <Field label="الأجر الشامل - اختياري" inputMode="decimal" value={form.totalWage} onChange={(value) => update('totalWage', value)} />
              <label className="md:col-span-2 text-sm font-bold text-[#293a50]">
                سبب انتهاء العلاقة
                <select value={form.endReason} onChange={(event) => update('endReason', event.target.value)} className="mt-2 h-12 w-full rounded-xl border border-[#eadfce] bg-white px-4 text-sm font-bold text-[#273950]">
                  {Object.entries(reasonLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
            </div>
          </div>

          <aside className="lo-surface rounded-[20px] p-7">
            <h2 className="legal-title text-xl font-bold text-[#293a50]">نتيجة تقديرية</h2>
            <div className="mt-6 space-y-4 text-sm">
              <Info label="مدة الخدمة بالأيام" value={`${result.service.days} يوم`} />
              <Info label="مدة الخدمة بالسنوات" value={result.service.years.toFixed(2)} />
              <Info label="الأجر المعتمد للحساب" value={money(result.wage)} />
              <Info label="المكافأة قبل نسب الاستقالة" value={money(result.fullAward)} />
              <Info label="نسبة الاستحقاق" value={`${Math.round(result.entitlementRate * 100)}%`} />
              <div className="rounded-2xl bg-[#f1eee8] p-5">
                <p className="text-sm text-[#697483]">قيمة المكافأة النهائية</p>
                <p className="mt-2 text-3xl font-bold text-[#293a50]">{money(result.award)}</p>
              </div>
              <p className="rounded-2xl border border-[#eadfce] bg-white p-4 leading-7 text-[#697483]">{result.entitlementNote}</p>
              <button type="button" onClick={() => window.print()} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#33455d] px-5 py-3 text-sm font-bold text-white">
                <Printer className="h-4 w-4" /> طباعة النتيجة
              </button>
            </div>
          </aside>
        </section>
      </main>
    </LaborOperationsLayout>
  );
};

const Field = ({ label, value, onChange, ...props }) => (
  <label className="text-sm font-bold text-[#293a50]">
    {label}
    <input {...props} value={value} onChange={(event) => onChange(event.target.value)} className="mt-2 h-12 w-full rounded-xl border border-[#eadfce] bg-white px-4 text-sm outline-none" />
  </label>
);

const Info = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3 border-b border-[#ece8e0] pb-3">
    <span className="text-[#697483]">{label}</span>
    <span className="font-bold text-[#293a50]">{value}</span>
  </div>
);

export default EndOfServiceCalculatorPage;
